<template>
  <div class="msg-product-card" @click="handleToDetailClick">
    <img v-lazy="$cvWebpFn(productInfo.goodsPic)" alt="" class="product-img">
    <div class="product-content">
      <p class="p-info p-info-title">{{ productInfo.goodsName }}</p>
      <p class="p-info">Listing: {{ productInfo.goodsId }}</p>
      <p class="p-info price">
        {{ productInfo.goodsUnit }}
        {{ productInfo.goodsPrice }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { showToast } from 'vant'
import {
  buildLiveChatBuyerProductClickSource,
  buildSellerSendProductLiveChatSource,
  type LiveChatFirstPage
} from '@/plugins/utils'

const props = defineProps<{
  productInfo: Msg.IMsgProduct,
  msgUserType?: number
  chatFirstPage?: LiveChatFirstPage
}>()

const { $toListingDetail } = useNuxtApp()

const normalizeCurrency = (value?: string): string => String(value || '').trim().toUpperCase()

const resolveProductCountry = (): region.IRegionItem | undefined => {
  const goodsUnit = normalizeCurrency(props.productInfo.goodsUnit)
  if (!goodsUnit) return

  const matchedCountries = useCountrys().value.filter(country =>
    [country.currencySymbol, country.currency]
      .some(currency => normalizeCurrency(currency) === goodsUnit)
  )

  return matchedCountries.length === 1 ? matchedCountries[0] : undefined
}

const resolveProductSource = (): string => {
  if (props.msgUserType === 1 || (props.msgUserType == null && props.productInfo.source)) {
    const buyerSource = buildLiveChatBuyerProductClickSource(props.productInfo.source)
    if (buyerSource) return buyerSource
  }

  const firstPage = props.chatFirstPage || useLiveChatFirstPage().value || 'messageCenter'
  return buildSellerSendProductLiveChatSource(firstPage)
}

const handleToDetailClick = () => {
  const { goodsId, goodsName } = props.productInfo
  const productCountry = resolveProductCountry()
  const currentCountryId = Number(useCurrentCountry().value)

  if (productCountry && productCountry.id !== currentCountryId) {
    showToast(`This product is from the ${productCountry.name} site.`)
    return
  }

  $toListingDetail(goodsId, { title: goodsName, source: resolveProductSource() })
}
</script>

<style lang="scss" scoped>

.msg-product-card{
  cursor: pointer;
  display: flex;
  width: 100%;
  font-size: 26px;
  border-radius: 10px;
  .product-img{
    width: 160px;
    min-width: 160px;
    height: 160px;
  }
  .product-content{
    padding-left: 20px;
    font-size: 24px;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    .p-info{
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 34px;
      color: #999;
      @include ellipsisMultiline(2);
    }
    .p-info-title{
      height: 70px;
      max-height: 70px;
      color: #333;
    }
    .price{
      margin-top: 6px;
      color: var(--default_color);
      font-weight: 600;
    }
  }
}
</style>
